import { usePal, PalHead, PalIcon, SubHead } from "./PalUI";

const COL_W = "minmax(78px,1fr)";

const Legend = ({ owners }) => {
  const { dim } = usePal();
  return (
    <ul className="flex flex-wrap gap-4" aria-label="Owner legend">
      {Object.entries(owners).map(([k, col]) => (
        <li key={k} className={`flex items-center gap-1.5 text-[12px] font-medium ${dim}`}>
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: col }} />{k}
        </li>
      ))}
    </ul>
  );
};

const LaneLabel = ({ children, color }) => (
  <div className="flex items-center gap-2 mb-4">
    <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: color }} />
    <span className="text-[12px] font-bold uppercase tracking-[0.14em]" style={{ color }}>{children}</span>
  </div>
);

const Timeline = ({ d }) => {
  const { ink, muted, dim, isDark, brand } = usePal();
  const grid = { gridTemplateColumns: `repeat(${d.columns.length}, ${COL_W})` };
  const rail = isDark ? "bg-white/15" : "bg-black/10";
  const lane = `rounded-2xl p-4 ${isDark ? "bg-white/[0.03] border border-white/10" : "bg-black/[0.02] border border-black/10"}`;
  return (
    <div className="overflow-x-auto -mx-2 px-2 pb-2">
      <div className="min-w-[1040px]" data-testid="pal-timeline">
        <div className="grid px-4" style={grid}>
          {d.columns.map((col) => (
            <div key={col} className={`text-[12px] font-semibold tabular-nums ${col === "D0" ? ink : dim}`}>{col}</div>
          ))}
        </div>

        <div className={`mt-3 ${lane}`} data-testid="pal-content-lane">
          <LaneLabel color={brand}>Content track</LaneLabel>
          <div className="relative">
            <div className={`absolute left-0 right-0 top-[5px] h-px ${rail}`} />
            <ol className="grid relative" style={grid} data-testid="pal-content-track">
              {d.content.map((m, i) => (
                <li key={i} className="pr-2" data-testid={`pal-milestone-${i}`}>
                  <span className="block w-[11px] h-[11px] rounded-full" style={{ backgroundColor: d.owners[m.owner] }} title={m.owner} />
                  <span className={`block mt-3 text-[12.5px] leading-snug ${ink}`}>{m.label}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <div className={`mt-3 ${lane}`} data-testid="pal-ad-lane">
          <LaneLabel color="#F59E0B">Advertising track</LaneLabel>
          <ol className="grid gap-y-2" style={grid} data-testid="pal-ad-track">
          {d.ads.map((a, i) => (
            <li
              key={i}
              className="rounded-xl px-3 py-2 mr-2"
              style={{ gridColumn: `${a.from + 1} / ${a.to + 2}`, backgroundColor: `${d.owners[a.owner]}22`, borderLeft: `3px solid ${d.owners[a.owner]}` }}
              data-testid={`pal-ad-path-${i}`}
            >
              <div className={`text-[12.5px] font-semibold leading-snug ${ink}`}>{a.label}</div>
              <div className={`text-[11px] ${muted}`}>{a.sub}</div>
            </li>
          ))}
        </ol>
        </div>
      </div>
    </div>
  );
};

const PalDelivery = () => {
  const { c, brand, ink, dim, hair } = usePal();
  const d = c.delivery;
  return (
    <section aria-label={d.eyebrow} data-testid="pal-delivery">
      <PalHead eyebrow={d.eyebrow} title={d.title} accentLine={d.accentLine} action={<Legend owners={d.owners} />} />
      <div className={`mt-10 pt-8 border-t ${hair}`}>
        <Timeline d={d} />
      </div>
      <div className="mt-12">
        <SubHead>Governance</SubHead>
        <ul className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="pal-governance">
          {d.governance.map((g) => (
            <li key={g.title} className="flex items-start gap-3">
              <PalIcon name={g.iconName} className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: brand }} />
              <div>
                <div className={`text-[15px] font-semibold leading-snug ${ink}`}>{g.title}</div>
                <div className={`text-[13px] ${dim}`}>{g.cadence}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default PalDelivery;
